/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { Button, FormGroup, Input } from "reactstrap";
import { LAYER_TYPE } from "../../../actions/Layers/constants";
import useConfig, { useTenantFormOptions } from "../../../actions/Tenants/config/configHook";
import { RootState } from "../../../reducers";
import { getFilledArrayOrDefault } from "../../../utils";
import useNavigateFrom from "../../../utils/useNavigateFrom";
import LayerSortableHeader, { HandleUnknownLayerFieldFilter } from "../../Helper/LayerSortableHeader";
import { Table, TableCell, TableHeaderCell } from "../../Helper/Table";
import { useSwitchTabContext } from "./fields/useSwitchTab";
import LayerListCta, { BulkLayerListCTA } from "./LayerListCta";


const DEFAULT_CHECK_CREATED_DAYS = Number(process.env.REACT_APP_LAYER_LIST_DEFAULT_CHECK_CREATED_DAYS) > 0
    ? Number(process.env.REACT_APP_LAYER_LIST_DEFAULT_CHECK_CREATED_DAYS)
    : 60;

const DEFAULT_LAYER_CREATED_DAYS = Number(process.env.REACT_APP_LAYER_LIST_DEFAULT_LAYER_CREATED_DAYS) > 0
    ? Number(process.env.REACT_APP_LAYER_LIST_DEFAULT_LAYER_CREATED_DAYS)
    : 90;

export const DataTestId = {
    TAB: "TAB",
    TAB_CONTENT: "TAB_CONTENT",
};

export default function LayerList() {
    const { layers, layersLoading: isLoading, filter } = useSwitchTabContext();
    const config = useConfig();
    const { navigateTo } = useNavigateFrom();
    const form_options = useTenantFormOptions();
    const stage = config.get_stage(filter.location);
    const user = useSelector<RootState, RootState["auth"]["user"]>((state) => state.auth.user);
    const { fields, activeTab, tab_category, selectedLayers, setSelectedLayers, selectedLayersArray, groupByProps } = useSwitchTabContext();

    // * Show default date filter message
    const layerTypeText = config.get_raw_layer_config(activeTab?.layer_type as LAYER_TYPE)?.text || "item";
    const checkLocations = getFilledArrayOrDefault(filter?.check_location).map((i) => config.get_raw_check_location(i)?.text || i);
    const hasExplicitDateFilter = Boolean(filter?.created_from || filter?.created_to || filter?.check_created_from || filter?.check_created_to);
    const defaultDateFilterMessage = useMemo(() => {
        if (hasExplicitDateFilter) return null;
        if (checkLocations.length > 0) {
            return <small className="text-muted">Shows <b>{layerTypeText}s</b> with <b>{checkLocations.join(", ")}</b> checks created in <b>last {DEFAULT_CHECK_CREATED_DAYS} days</b>.</small>;
        }
        return <small className="text-muted">Shows <b>{layerTypeText}s</b> created in <b>last {DEFAULT_LAYER_CREATED_DAYS} days</b>.</small>;
    }, [hasExplicitDateFilter, checkLocations, layerTypeText]);

    const toggleSelectedLayer = (layer_id) => setSelectedLayers((prev) => ({ ...prev, [layer_id]: !prev[layer_id] }));

    const selectALlorNothing = () => {
        if (Object.keys(selectedLayers).length > 0) {
            setSelectedLayers({});
            return;
        }

        const newSelected = layers.results.reduce((acc, i) => {
            acc[i.id] = true;
            return acc;
        }, {});

        setSelectedLayers(newSelected);
    };

    // Create layer table
    const getValue = (p) => fields.map((field, index) => (
        <TableCell key={index}>
            {field
                .set_value({
                    config,
                    layer: p,
                    check: null, // * To be used in check tables and layer overview
                    fruit: null, // * Fruit list is not available on overview screen
                    children: null, // * children is only available on layer index page
                    form_options, // * used to translate form values to labels
                    user
                })
                .display_component()}
        </TableCell>
    ));
    // * Setup LayerStub used to fetch checks applicable for this fruit and layer type
    const layerStubForChecks: any = {
        type: filter?.layer_type ?? activeTab?.layer_type ?? null,
        fruit_type: getFilledArrayOrDefault(filter?.fruit_type).length === 1 ? getFilledArrayOrDefault(filter?.fruit_type)[0] : null,
    };
    const canStartCheckFromOverview = config
        .get_check_types_config(layerStubForChecks)
        .some((l: any) => getFilledArrayOrDefault(l.start_on_overview_tabs).includes(activeTab?.value));
    const hasActionColumn = Boolean(activeTab?.cta_form_title) || canStartCheckFromOverview;

    // if activeTab?.cta_form_title or overview startable checks exist, add an action header
    const header = useMemo(() => {
        const headers = getFilledArrayOrDefault(fields).map((field, index) => <LayerSortableHeader key={index} field={field} />);

        if (hasActionColumn && activeTab?.cta_form_title) {
            headers.push(
                <TableHeaderCell key="actions" sticky>
                    {activeTab?.cta_form_title || "Actions"}
                </TableHeaderCell>
            );
        }

        return headers;
    }, [fields, hasActionColumn, activeTab?.cta_form_title]);

    const [groups, layersWithGroupKey] = groupByProps.getGroups(getFilledArrayOrDefault(layers?.results));

    const tableHeader = useMemo(() => (
        <tr>
            {hasActionColumn && (
                <TableHeaderCell>
                    <FormGroup check>
                        <Input
                            onChange={selectALlorNothing}
                            checked={Boolean(
                                selectedLayersArray.length > 0
                                && layers.results.length > 0
                                && selectedLayersArray.length === layers.results.length
                            )}
                            type="checkbox"
                        />
                    </FormGroup>
                </TableHeaderCell>
            )}

            {header}
        </tr>
    ), [header, hasActionColumn, selectedLayers, layers?.results?.length]);

    const selectGroup = (key, selected) => setSelectedLayers((prev) => layersWithGroupKey.filter((i) => i.group_key === key).reduce((acc, i) => ({
        ...acc,
        [i.id]: selected,
    }), prev));

    const allInGroupSelected = (key) => layersWithGroupKey.filter(((i) => i.group_key === key)).every((i) => selectedLayers[i.id]);

    if (!layers || !stage || !fields) {
        return null;
    }

    return (
        <div className="pt-4 d-flex flex-column">
            <div>
                <Table
                    header={tableHeader}
                    isLoading={isLoading}
                    count={layers.count}
                    customLimit={stage?.limit ? Number(stage?.limit) : undefined}
                >
                    {!groups && (
                        <tbody>
                            {layers.results.map((item, index) => (
                                <tr key={index} title={item.id}>
                                    {hasActionColumn && (
                                        <TableCell>
                                            <FormGroup check>
                                                <Input
                                                    onChange={() => toggleSelectedLayer(item.id)}
                                                    checked={Boolean(selectedLayers[item.id])}
                                                    type="checkbox"
                                                />
                                            </FormGroup>
                                        </TableCell>
                                    )}

                                    {getValue(item)}

                                    {hasActionColumn && activeTab?.cta_form_title && (
                                        <TableCell className="text-end" sticky>
                                            <div className="d-flex gap-1 flex-wrap justify-content-end">
                                                {activeTab?.cta_form_title && (
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        onClick={() => navigateTo(`/layer/tab/${tab_category}/${activeTab?.value}`, true, { cta_layer_id: item.id }, true)}
                                                    >
                                                        {activeTab?.cta_form_title}
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    )}

                    {!!groups && groups.map((group) => (
                        <tbody key={group.group_key} className="border-top">
                            <tr>
                                <td>
                                    <div className="pt-2"></div>
                                </td>
                            </tr>

                            <tr>
                                {hasActionColumn && (
                                    <TableCell>
                                        <FormGroup check>
                                            <Input
                                                onChange={() => selectGroup(group.group_key, !allInGroupSelected(group.group_key))}
                                                checked={Boolean(allInGroupSelected(group.group_key))}
                                                type="checkbox"
                                            />
                                        </FormGroup>
                                    </TableCell>
                                )}

                                <TableCell colSpan={fields.length + (hasActionColumn ? 1 : 0)}>
                                    <div className="d-flex pt-2 pb-2">
                                        <div className="fw-bold text-muted" css={css`min-width: 2rem;`}>{group.pallets}</div>

                                        {group.number_of_boxes > 0 && (
                                            <div className="ms-2 fw-bold text-muted" css={css`min-width: 4rem;`}>
                                                {(group.number_of_boxes > 0 ? `${group.number_of_boxes}b` : "-")}
                                            </div>
                                        )}

                                        <div className="fw-bold ">{group.group_label}</div>
                                    </div>
                                </TableCell>
                            </tr>

                            {group.items.map((item, index) => (
                                <tr key={index} title={item.id}>
                                    {hasActionColumn && (
                                        <TableCell>
                                            <FormGroup check>
                                                <Input
                                                    onChange={() => toggleSelectedLayer(item.id)}
                                                    checked={Boolean(selectedLayers[item.id])}
                                                    type="checkbox"
                                                />
                                            </FormGroup>
                                        </TableCell>
                                    )}

                                    {getValue(item)}

                                    {hasActionColumn && activeTab?.cta_form_title && (
                                        <TableCell className="text-end" sticky>
                                            <div className="d-flex gap-1 flex-wrap justify-content-end">
                                                {activeTab?.cta_form_title && (
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        onClick={() => navigateTo(`/layer/tab/${tab_category}/${activeTab?.value}`, true, { cta_layer_id: item.id }, true)}
                                                    >
                                                        {activeTab?.cta_form_title}
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                </tr>
                            ))}

                            <tr>
                                <td>
                                    <div className="pt-2"></div>
                                </td>
                            </tr>
                        </tbody>
                    ))}
                </Table>
                {defaultDateFilterMessage && (
                    <div className="text-muted pt-2">
                        {defaultDateFilterMessage}
                    </div>
                )}
            </div>

            <LayerListCta />

            <BulkLayerListCTA selectedLayers={selectedLayersArray} deselectAll={() => setSelectedLayers({})} />

            <HandleUnknownLayerFieldFilter />
        </div>
    );
}
